import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { ShieldAlert } from 'lucide-react';
import { useCurrentUser } from '../hooks/useCurrentUser';
import SEOHead from '../components/SEOHead';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/* Editorial "Próximamente" — asymmetric split, oversized italic title,
   terracotta serif accents, optional newsletter capture, admin shortcut. */
const ComingSoonPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { isAdmin } = useCurrentUser();
  const [settings, setSettings] = useState(null);
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/general-settings/general`);
        setSettings(r.data);
      } catch {
        /* keep defaults */
      }
    })();
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || submitting) return;
    setSubmitting(true);
    try {
      // Best-effort capture; ignore errors silently so the UI stays focused.
      await axios.post(`${API}/newsletter/subscribe`, { email: email.trim() }).catch(() => {});
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  // Status text in general settings is authored once (in Spanish), so it only
  // applies to the Spanish storefront; other languages use the translations.
  const status = (i18n.language || 'es').startsWith('es') ? settings?.status : null;
  const title = status?.comingSoonTitle || t('comingSoon.title');
  const description =
    status?.comingSoonDescription ||
    (settings ? t('comingSoon.description') : t('comingSoon.descriptionInitial'));

  return (
    <>
      <SEOHead title={title} description={description} noIndex />
      <main
        data-testid="coming-soon-page"
      style={{
        minHeight: '100vh',
        background: 'var(--bg)',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      {/* Decorative oversized italic glyph */}
      <span
        aria-hidden="true"
        style={{
          position: 'absolute',
          right: '-6vw',
          top: '-2vw',
          fontFamily: 'var(--font-display)',
          fontStyle: 'italic',
          fontVariationSettings: "'opsz' 144, 'SOFT' 100",
          fontSize: '58vw',
          lineHeight: 0.8,
          color: 'var(--ink)',
          opacity: 0.045,
          pointerEvents: 'none',
          letterSpacing: '-6px',
          userSelect: 'none',
        }}
      >
        ✦
      </span>

      <div
        className="cs-grid"
        style={{
          width: '100%',
          maxWidth: '1280px',
          margin: '0 auto',
          padding: '80px 40px',
          display: 'grid',
          gridTemplateColumns: '1.15fr 0.85fr',
          gap: '90px',
          alignItems: 'center',
          position: 'relative',
        }}
      >
        {/* Left — typography */}
        <section style={{ position: 'relative' }}>
          <img
            src="/logo-cimonamia.png"
            alt="Cimonamia"
            style={{ height: '38px', marginBottom: '56px', display: 'block' }}
          />

          <span
            className="eyebrow"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '12px',
              color: 'var(--ink-soft)',
              marginBottom: '28px',
            }}
          >
            <span
              style={{
                display: 'inline-block',
                width: '36px',
                height: '1px',
                background: 'var(--terracotta)',
              }}
            />
            {t('comingSoon.eyebrow')}
          </span>

          <h1
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'clamp(56px, 9.5vw, 152px)',
              fontWeight: 300,
              lineHeight: 0.92,
              letterSpacing: '-3px',
              color: 'var(--ink)',
              marginBottom: '36px',
            }}
            data-testid="coming-soon-title"
          >
            {title.split(' ').map((w, i, arr) => (
              <span key={i}>
                {i === arr.length - 1 ? <em>{w}</em> : w}
                {i < arr.length - 1 && ' '}
              </span>
            ))}
          </h1>

          <p
            style={{
              fontFamily: 'var(--font-body)',
              fontSize: 'clamp(16px, 1.35vw, 19px)',
              lineHeight: 1.75,
              maxWidth: '520px',
              color: 'var(--ink-soft)',
              marginBottom: '48px',
            }}
            data-testid="coming-soon-description"
          >
            {description}
          </p>

          {/* Newsletter / waitlist */}
          {!submitted ? (
            <form
              onSubmit={onSubmit}
              className="cs-form"
              style={{
                display: 'flex',
                alignItems: 'stretch',
                gap: 0,
                maxWidth: '480px',
                borderBottom: '1px solid var(--ink)',
              }}
              data-testid="coming-soon-form"
            >
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('comingSoon.emailPlaceholder')}
                aria-label={t('comingSoon.emailAria')}
                style={{
                  flex: 1,
                  background: 'transparent',
                  border: 0,
                  outline: 'none',
                  padding: '14px 4px',
                  fontFamily: 'var(--font-body)',
                  fontSize: '15px',
                  color: 'var(--ink)',
                }}
              />
              <button
                type="submit"
                disabled={submitting}
                style={{
                  background: 'transparent',
                  border: 0,
                  fontFamily: 'var(--font-body)',
                  fontSize: '11px',
                  letterSpacing: '3px',
                  textTransform: 'uppercase',
                  color: 'var(--ink)',
                  cursor: submitting ? 'wait' : 'pointer',
                  padding: '0 16px',
                  fontWeight: 500,
                }}
              >
                {submitting ? '...' : t('comingSoon.notifyMe')}
              </button>
            </form>
          ) : (
            <p
              data-testid="coming-soon-success"
              style={{
                fontFamily: 'var(--font-display)',
                fontStyle: 'italic',
                fontVariationSettings: "'opsz' 60, 'SOFT' 100",
                fontSize: '20px',
                color: 'var(--terracotta)',
                maxWidth: '480px',
              }}
            >
              {t('comingSoon.thanks')}
            </p>
          )}

          <p
            style={{
              marginTop: '14px',
              fontFamily: 'var(--font-body)',
              fontSize: '12px',
              color: 'var(--ink-soft)',
              maxWidth: '480px',
            }}
          >
            {t('comingSoon.privacyNote')}
          </p>

          {/* Admin shortcut — discreet */}
          {isAdmin && (
            <button
              type="button"
              onClick={() => navigate('/admin')}
              data-testid="coming-soon-admin"
              className="link-underline"
              style={{
                marginTop: '56px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                background: 'transparent',
                border: 0,
                fontFamily: 'var(--font-body)',
                fontSize: '11px',
                letterSpacing: '3px',
                textTransform: 'uppercase',
                color: 'var(--ink-soft)',
                cursor: 'pointer',
                padding: 0,
              }}
            >
              <ShieldAlert style={{ width: 14, height: 14 }} strokeWidth={1.4} />
              {t('comingSoon.adminAccess')}
            </button>
          )}
        </section>

        {/* Right — editorial portrait & overlay caption */}
        <aside style={{ position: 'relative' }} className="cs-visual">
          <figure style={{ margin: 0, position: 'relative' }}>
            <img
              src="https://images.unsplash.com/photo-1558769132-cb1aea458c5e?w=1200&q=80"
              alt={t('comingSoon.imageAlt')}
              loading="eager"
              decoding="async"
              style={{
                width: '100%',
                aspectRatio: '3 / 4',
                objectFit: 'cover',
                display: 'block',
                filter: 'saturate(0.95) contrast(1.02)',
              }}
            />
            {/* Vertical caption */}
            <figcaption
              style={{
                position: 'absolute',
                top: '24px',
                right: '-14px',
                transform: 'rotate(90deg)',
                transformOrigin: 'right top',
                fontFamily: 'var(--font-body)',
                fontSize: '10px',
                letterSpacing: '4px',
                textTransform: 'uppercase',
                color: 'var(--ink-soft)',
                whiteSpace: 'nowrap',
              }}
            >
              {t('comingSoon.caption')}
            </figcaption>
            {/* Italic overlay quote */}
            <div
              aria-hidden="true"
              style={{
                position: 'absolute',
                left: '-32px',
                bottom: '40px',
                background: 'var(--bg)',
                padding: '20px 28px',
                maxWidth: '78%',
                boxShadow: '0 24px 60px -24px rgba(26,24,20,0.18)',
              }}
            >
              <span
                style={{
                  fontFamily: 'var(--font-display)',
                  fontStyle: 'italic',
                  fontVariationSettings: "'opsz' 60, 'SOFT' 80",
                  fontSize: 'clamp(18px, 1.4vw, 22px)',
                  lineHeight: 1.3,
                  color: 'var(--ink)',
                  letterSpacing: '-0.2px',
                }}
              >
                {t('comingSoon.quote')}
              </span>
            </div>
          </figure>
        </aside>
      </div>

      {/* Bottom legal strip */}
      <footer
        style={{
          position: 'absolute',
          bottom: '32px',
          left: 0,
          right: 0,
          textAlign: 'center',
          fontFamily: 'var(--font-body)',
          fontSize: '11px',
          letterSpacing: '2.5px',
          textTransform: 'uppercase',
          color: 'var(--ink-soft)',
        }}
      >
        © {new Date().getFullYear()} Cimonamia
      </footer>

      {/* Responsive */}
      <style>{`
        @media (max-width: 900px) {
          .cs-grid {
            grid-template-columns: 1fr !important;
            gap: 56px !important;
            padding: 100px 28px 120px !important;
          }
          .cs-visual { order: -1; max-width: 360px; margin: 0 auto; }
          .cs-visual figcaption { display: none; }
        }
      `}</style>
      </main>
    </>
  );
};

export default ComingSoonPage;
